import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { fakeAuth, Session, AuthError } from '../lib/fakeAuth';

type Role = 'admin' | 'user' | 'product-manager' | 'developer' | 'entrepreneur' | 'tech-entrepreneur' | 'consultant' | 'project-manager';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  userRoles: Role[];
  isAdmin: boolean;
  isNewSession: boolean;
  signIn: (email: string, password: string) => Promise<{
    error: AuthError | null;
    data: Session | null;
  }>;
  signUp: (email: string, password: string, userData?: any) => Promise<{
    error: AuthError | null;
    data: Session | null;
  }>;
  signOut: () => Promise<void>;
  hasRole: (role: Role) => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// List of allowed admin emails
const ADMIN_EMAILS = [
  'admin@cortex-ai.es'
];

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<Role[]>(['user']);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isNewSession, setIsNewSession] = useState(false);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data } = await fakeAuth.getSession();
        setSession(data.session);
        setUser(data.session?.user ?? null);
        
        // Check if user is admin
        if (data.session?.user?.email && ADMIN_EMAILS.includes(data.session.user.email)) {
          setUserRoles(['admin', 'user']);
          setIsAdmin(true);
        } else if (data.session?.user?.user_metadata?.role) {
          setUserRoles(['user', data.session.user.user_metadata.role as Role]);
        }
      } catch (error) {
        console.error("Error getting initial session:", error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = fakeAuth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Update roles when auth state changes
      if (session?.user?.email && ADMIN_EMAILS.includes(session.user.email)) {
        setUserRoles(['admin', 'user']);
        setIsAdmin(true);
      } else if (session?.user?.user_metadata?.role) {
        setUserRoles(['user', session.user.user_metadata.role as Role]);
        setIsAdmin(false);
      } else {
        setUserRoles(['user']);
        setIsAdmin(false);
      }
      
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      const { data, error } = await fakeAuth.signInWithPassword({
        email,
        password,
      });
      
      if (!error) {
        setSession(data.session);
        setUser(data.user);
        setIsNewSession(true);
        
        // Check if user is admin
        if (ADMIN_EMAILS.includes(email)) {
          setUserRoles(['admin', 'user']);
          setIsAdmin(true);
        } else if (data.user?.user_metadata?.role) {
          setUserRoles(['user', data.user.user_metadata.role as Role]);
          setIsAdmin(false);
        }
      }
      
      return { data: data.session, error };
    } catch (error) {
      console.error("Error in signIn:", error);
      return { data: null, error: error as AuthError };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, userData?: any) => {
    try {
      setLoading(true);
      
      // Prevent admin role assignment during signup
      if (userData?.role === 'admin') {
        return { 
          data: null, 
          error: { message: 'No se puede crear una cuenta de administrador' } 
        };
      }
      
      const { data, error } = await fakeAuth.signUp({
        email,
        password,
        options: {
          data: userData,
        },
      });
      
      if (!error) {
        setSession(data.session);
        setUser(data.user);
        setIsNewSession(true);
        
        // Set initial role as user
        setUserRoles(['user', userData?.role || 'user']);
        setIsAdmin(false);
      }
      
      return { data: data.session, error };
    } catch (error) {
      console.error("Error in signUp:", error);
      return { data: null, error: error as AuthError };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await fakeAuth.signOut();
      setSession(null);
      setUser(null);
      setUserRoles(['user']);
      setIsAdmin(false);
      setIsNewSession(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const hasRole = (role: Role) => {
    return userRoles.includes(role);
  };

  const value = {
    session,
    user,
    loading,
    userRoles,
    isAdmin,
    isNewSession,
    signIn,
    signUp,
    signOut,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};